import type {
  AccessCatalogItemPublic,
  OnboardingPick,
  OnboardingRequest,
} from '@accounts/shared';

export interface OnboardingPersonDraft {
  name: string;
  email: string;
  title: string;
  isAdmin: boolean;
}

/** Administrators may give any live row; HR may give only explicitly delegated rows. */
export function onboardingMode(
  item: Pick<AccessCatalogItemPublic, 'hrCanGrant'>,
  hrOnly: boolean,
): OnboardingPick['mode'] {
  return !hrOnly || item.hrCanGrant ? 'direct' : 'request';
}

export function onboardingCounts(
  items: readonly AccessCatalogItemPublic[],
  selectedIds: ReadonlySet<string>,
  hrOnly: boolean,
): { givenNow: number; requestsToAdmin: number; itSetupItems: number } {
  let givenNow = 0;
  let requestsToAdmin = 0;
  let itSetupItems = 0;
  for (const item of items) {
    if (!selectedIds.has(item.id)) continue;
    if (onboardingMode(item, hrOnly) === 'request') {
      requestsToAdmin += 1;
      continue;
    }
    givenNow += 1;
    if (item.application === null) itSetupItems += 1;
  }
  return { givenNow, requestsToAdmin, itSetupItems };
}

export function buildOnboardingRequest(
  person: OnboardingPersonDraft,
  items: readonly AccessCatalogItemPublic[],
  selectedIds: ReadonlySet<string>,
  hrOnly: boolean,
): OnboardingRequest {
  return {
    name: person.name.trim(),
    email: person.email.trim(),
    ...(person.title.trim() === '' ? {} : { title: person.title.trim() }),
    isAdmin: hrOnly ? false : person.isAdmin,
    picks: items.flatMap((item): OnboardingPick[] =>
      selectedIds.has(item.id)
        ? [{ catalogItemId: item.id, mode: onboardingMode(item, hrOnly) }]
        : [],
    ),
  };
}

/** The picker groups suite applications first, then top-level hierarchy node, then unfiled. */
export function onboardingGroups(
  items: readonly AccessCatalogItemPublic[],
): { key: string; name: string; items: AccessCatalogItemPublic[] }[] {
  const groups = new Map<string, { key: string; name: string; items: AccessCatalogItemPublic[] }>();
  for (const item of items) {
    const top = item.path[0];
    const group =
      item.application !== null
        ? { key: '!apps', name: 'Suite applications' }
        : top === undefined
          ? { key: '~unfiled', name: 'Other outside systems' }
          : { key: top.id, name: top.name };
    const found = groups.get(group.key) ?? { ...group, items: [] };
    found.items.push(item);
    groups.set(group.key, found);
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      items: [...group.items].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      if (a.key === '!apps') return b.key === '!apps' ? 0 : -1;
      if (b.key === '!apps') return 1;
      if (a.key === '~unfiled') return b.key === '~unfiled' ? 0 : 1;
      if (b.key === '~unfiled') return -1;
      return a.name.localeCompare(b.name);
    });
}
